import { Component, type ErrorInfo, type ReactNode } from "react";
import { Btn } from "../ui";
import { TX } from "../../lib/texte";

interface Props {
  children: ReactNode;
  /** Ersetzt den Standardsatz — die Bühne des Editors sagt etwas anderes als eine Liste. */
  titel?: string;
  hinweis?: string;
  /** Zusätzlicher Knopf, z. B. „Letzte Fassung als JSON sichern" (§5.7.3). */
  aktion?: ReactNode;
}
interface State {
  fehler: Error | null;
}

/**
 * Der Auffangkorb. Ohne ihn nimmt ein einziger Fehler beim Zeichnen den
 * ganzen Baum mit und hinterlässt eine weiße Seite — ohne jeden Hinweis,
 * was zu tun wäre.
 *
 * Muss eine Klasse sein: `componentDidCatch` hat in Funktionskomponenten bis
 * heute keine Entsprechung.
 *
 * Im Editor stehen ZWEI davon (§5.7.3): einer um die Bühne, einer um die
 * beiden Leisten. Eine kaputte Balkengeometrie darf den Inspektor nicht
 * mitnehmen — der Inspektor ist der Ort, an dem man sie reparieren kann.
 * Der Satz „Deine Antworten sind gespeichert" aus dem Fragenbüro wäre hier
 * eine Lüge; stattdessen nennt der Aufrufer seinen eigenen Satz und hängt
 * über `aktion` den JSON-Rettungsknopf an.
 */
export class ScreenBoundary extends Component<Props, State> {
  override state: State = { fehler: null };

  static getDerivedStateFromError(fehler: Error): State {
    return { fehler };
  }

  override componentDidCatch(fehler: Error, info: ErrorInfo): void {
    // In die Konsole, nicht zu einem Fremdanbieter: es gibt hier keinen
    // Fehlerdienst, und im Wortlaut könnten Projektdaten stehen.
    console.error("[Zeitplaner] Bildschirm abgestürzt:", fehler, info.componentStack);
  }

  override render() {
    if (!this.state.fehler) return this.props.children;
    return (
      <div className="huelle" role="alert">
        <div className="card" style={{ padding: 28, textAlign: "center" }}>
          <h1 className="seitentitel">{this.props.titel ?? TX.fehler.umgefallen}</h1>
          <p className="unterzeile">{this.props.hinweis ?? TX.fehler.umgefallenHinweis}</p>
          <p className="font-mono" style={{ fontSize: 12, color: "var(--text-mute)", wordBreak: "break-word", marginBottom: 20 }}>
            {this.state.fehler.message}
          </p>
          <div className="reihe" style={{ justifyContent: "center" }}>
            <Btn type="button" icon="arrow-clockwise" onClick={() => window.location.reload()}>
              {TX.fehler.neuLaden}
            </Btn>
            <Btn type="button" variant="ghost" onClick={() => this.setState({ fehler: null })}>
              {TX.fehler.nochmal}
            </Btn>
            {this.props.aktion}
          </div>
        </div>
      </div>
    );
  }
}
